import { useLazyQuery } from "@apollo/client";
import Preview from "@mui/icons-material/Preview";
import Remove from "@mui/icons-material/Remove";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import {
  GridColDef,
  GridSortModel,
  GridRowOrderChangeParams,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { DocumentPreview } from "components/Document";
import { HoverAction } from "components/HoverAction";
import { DocumentType } from "generated-graphql/graphql";
import { usePaginationModel } from "hooks/usePaginationModel";
import { startCase } from "lodash-es";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import { useMemo, useState } from "react";
import { documentTypeToLabel } from "util/constants";
import { prettyPrintDateMed } from "util/dates";
import { useCommunicationDocuments } from "../hooks/useCommunicationDocuments";
import { CommunicationDocument } from "../types";

export const DocumentsDataGrid = ({
  documentIds,
  removeDocument,
  onReorderDocuments,
}: {
  documentIds: string[];
  removeDocument: ((id: string) => void) | null;
  onReorderDocuments?: (newOrder: string[]) => void;
}) => {
  const alerts = useAlerts();
  const pageSize = 10;
  const [paginationModel, setPaginationModel] = usePaginationModel(pageSize);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "createdAt", sort: "desc" },
  ]);

  const [previewFileUrl, setPreviewFileUrl] = useState<string | undefined>(
    undefined
  );
  const closePreview = () => setPreviewFileUrl(undefined);

  const [getDocumentDownloadLink] = useLazyQuery(
    GET_DOCUMENT_DOWNLOAD_LINK_QUERY,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (!data?.getDocumentDownloadLink) {
          return alerts.error("Error fetching download link");
        }
      },
      onError: (e) => {
        alerts.error("Error fetching download link", e);
      },
    }
  );

  // For drag-and-drop reordering, we need to show all documents at once
  // instead of using pagination
  const { data, previousData, loading } = useCommunicationDocuments({
    documentIds,
    page: onReorderDocuments ? undefined : paginationModel.page,
    pageSize: onReorderDocuments ? undefined : paginationModel.pageSize, // Large page size to show all documents
    sort: onReorderDocuments ? [] : [...sortModel], // No sorting when reordering
  });

  const handleRowOrderChange = (params: GridRowOrderChangeParams) => {
    if (!onReorderDocuments) return;

    const { oldIndex, targetIndex } = params;
    const items = data?.documents.items ?? [];

    // Create a new array with the reordered items
    const newOrder = [...items];
    const [movedItem] = newOrder.splice(oldIndex, 1);
    newOrder.splice(targetIndex, 0, movedItem);

    // Extract the document IDs in the new order
    const newDocumentIds = newOrder.map((item) => item.id);
    onReorderDocuments(newDocumentIds);
  };

  const columns: GridColDef<CommunicationDocument>[] = useMemo(() => {
    return [
      {
        field: "title",
        headerName: "Name",
        flex: 1.5,
        minWidth: 200,
        sortable: true,
        renderCell(params) {
          return (
            <Tooltip title={params.row.title}>
              <Box
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {params.row.title}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "documentType",
        headerName: "Document Type",
        flex: 0.8,
        minWidth: 120,
        sortable: true,
        valueGetter(_, row) {
          return documentTypeToLabel(row.documentType);
        },
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(DocumentType),
        enumPresentationFunction: (enumValue: string) =>
          documentTypeToLabel(enumValue as DocumentType),
      },
      {
        field: "fileExtension",
        headerName: "Format",
        flex: 0.5,
        minWidth: 80,
        sortable: true,
      },
      {
        field: "facility",
        headerName: "Facility",
        filterKeyType: "facility",
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell(params) {
          const facilities = params.row.facilities ?? [];
          return (
            <Stack>
              {facilities.slice(0, 3).map((facility) => (
                <Typography key={facility.id} variant="body2">
                  {facility.name}
                </Typography>
              ))}
              {facilities.length > 3 && (
                <Typography variant="body2">...</Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Uploaded On",
        flex: 0.6,
        minWidth: 120,
        sortable: true,
        valueGetter(_, row) {
          return prettyPrintDateMed(row.createdAt);
        },
      },
      {
        field: "actions",
        type: "actions",
        flex: 0.3,
        minWidth: 80,
        getActions: ({ row }) => {
          const actions = [
            <Tooltip title="Preview Document" key="preview">
              <HoverAction
                onClick={async () => {
                  const { data } = await getDocumentDownloadLink({
                    variables: { id: row.id },
                  });

                  if (
                    data?.getDocumentDownloadLink === null ||
                    data?.getDocumentDownloadLink === undefined
                  ) {
                    alerts.error("Error fetching file url");
                    return;
                  }
                  setPreviewFileUrl(data.getDocumentDownloadLink);
                }}
                label="Preview Document"
                icon={<Preview fontSize="small" color="primary" />}
              />
            </Tooltip>,
          ];

          if (removeDocument) {
            actions.push(
              <Tooltip title="Remove" key="remove">
                <HoverAction
                  label="Remove"
                  icon={<Remove fontSize="small" color="primary" />}
                  onClick={() => removeDocument(row.id)}
                />
              </Tooltip>
            );
          }

          return actions;
        },
      },
    ];
  }, [removeDocument, getDocumentDownloadLink, alerts]);

  const items = useMemo(() => {
    const rawItems =
      data?.documents.items ?? previousData?.documents.items ?? [];
    // Add __reorder__ field to customize drag preview text (shows title instead of ID)
    return rawItems.map((item) => ({
      ...item,
      __reorder__: item.title,
    }));
  }, [data?.documents.items, previousData?.documents.items]);
  const count = useMemo(() => {
    return data?.documents.count ?? previousData?.documents.count ?? 0;
  }, [data?.documents.count, previousData?.documents.count]);

  // When reordering is enabled, disable sorting and pagination
  const enableReordering = !!onReorderDocuments;
  const showPagination = !enableReordering && count > pageSize;

  return (
    <>
      <DataGrid
        columns={columns}
        rows={items}
        rowCount={count}
        loading={loading}
        isRowSelectable={() => false}
        // Enable drag-and-drop row reordering
        rowReordering={enableReordering}
        onRowOrderChange={handleRowOrderChange}
        // Disable sorting when reordering
        disableColumnSorting={enableReordering}
        // Pagination settings
        pagination={showPagination}
        paginationMode={showPagination ? "server" : undefined}
        paginationModel={showPagination ? paginationModel : undefined}
        onPaginationModelChange={
          showPagination ? setPaginationModel : undefined
        }
        // Sorting settings
        sortingMode={enableReordering ? undefined : "server"}
        sortModel={enableReordering ? [] : sortModel}
        onSortModelChange={enableReordering ? undefined : setSortModel}
        hideFooter={enableReordering ? true : count <= pageSize}
        // Add custom styling for drag-and-drop
        sx={{
          ...(enableReordering && {
            "& .MuiDataGrid-row": {
              cursor: "grab",
            },
            "& .MuiDataGrid-row:active": {
              cursor: "grabbing",
            },
          }),
        }}
      />
      {!!previewFileUrl && (
        <Dialog
          open
          onClose={closePreview}
          fullWidth
          maxWidth="lg"
          title="Document Preview"
          noPrimaryAction
        >
          <DocumentPreview fileUrl={previewFileUrl} />
        </Dialog>
      )}
    </>
  );
};
